import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import Typography from '@mui/material/Typography';
import { Translate, translateDateFormat } from '../../../../shared/LanguageService';
import { CustomerBooking } from '../../types';
import { centerOf, ScreenPoint, useExpandFromPoint } from '../../shared/state/expandFromPoint';

type SimultaneousBookingsDialogProps = {
    bookings: CustomerBooking[] | null;
    open: boolean;
    from: ScreenPoint | null;
    showBrokerFirm: boolean;
    onSelect: (booking: CustomerBooking, from: ScreenPoint) => void;
    onClose: () => void;
};

const TitleId = 'simultaneous-bookings-title';

function timeRange(booking: CustomerBooking) {
    return `${translateDateFormat(new Date(booking.start), 'HH:mm')} – ${translateDateFormat(
        new Date(booking.end),
        'HH:mm'
    )}`;
}

function hasEnded(booking: CustomerBooking) {
    return new Date(booking.end).getTime() < Date.now();
}

type SimultaneousBookingProps = {
    booking: CustomerBooking;
    showBrokerFirm: boolean;
    onSelect: (booking: CustomerBooking, from: ScreenPoint) => void;
};

function SimultaneousBooking({ booking, showBrokerFirm, onSelect }: SimultaneousBookingProps) {
    let details = [booking.product, booking.performedBy].filter(Boolean).join(' · ');

    return (
        <ListItemButton
            onClick={(event) => onSelect(booking, centerOf(event.currentTarget))}
            sx={{
                alignItems: 'flex-start',
                gap: 2,
                mb: 1,
                borderRadius: 1,
                borderLeft: 3,
                borderColor: hasEnded(booking) ? 'grey.400' : 'secondary.main',
                backgroundColor: 'grey.50'
            }}
        >
            <Typography
                variant="body2"
                sx={{
                    fontWeight: 700,
                    fontVariantNumeric: 'tabular-nums',
                    whiteSpace: 'nowrap',
                    pt: '1px'
                }}
            >
                {timeRange(booking)}
            </Typography>
            <Box sx={{ minWidth: 0 }}>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {booking.address || `${booking.orderCollectionNumber}:${booking.orderNumber}`}
                </Typography>
                {details && (
                    <Typography variant="body2" color="text.secondary">
                        {details}
                    </Typography>
                )}
                {showBrokerFirm && booking.brokerFirmName && (
                    <Typography variant="body2" color="text.secondary">
                        {booking.brokerFirmName}
                    </Typography>
                )}
            </Box>
        </ListItemButton>
    );
}

function SimultaneousBookingsDialog({
    bookings,
    open,
    from,
    showBrokerFirm,
    onSelect,
    onClose
}: SimultaneousBookingsDialogProps) {
    const expandFrom = useExpandFromPoint(from);

    return (
        <Dialog
            {...expandFrom}
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            aria-labelledby={TitleId}
        >
            {bookings && (
                <>
                    <DialogTitle id={TitleId} sx={{ pb: 0 }}>
                        {Translate('customer-calendar/simultaneous/title', {
                            count: bookings.length
                        })}
                    </DialogTitle>
                    <DialogContent>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ textTransform: 'capitalize', mb: 2 }}
                        >
                            {translateDateFormat(new Date(bookings[0].start), 'PPPP')}
                        </Typography>
                        <List disablePadding>
                            {bookings.map((booking) => (
                                <SimultaneousBooking
                                    key={booking.id}
                                    booking={booking}
                                    showBrokerFirm={showBrokerFirm}
                                    onSelect={onSelect}
                                />
                            ))}
                        </List>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={onClose}>
                            {Translate('customer-calendar/booking/close')}
                        </Button>
                    </DialogActions>
                </>
            )}
        </Dialog>
    );
}

export default SimultaneousBookingsDialog;
